import { useCallback, useEffect, useRef, useState } from "react";

interface UseFileDropOptions {
	/**
	 * Callback fired when files are dropped on the element
	 */
	onDropped?: (files: File[]) => void;

	/**
	 * Whether to enable drag and drop functionality
	 * @default true
	 */
	enabled?: boolean;

	/**
	 * Whether to cancel drag operation when Escape key is pressed
	 * @default false
	 */
	cancelOnEscape?: boolean;

	/**
	 * Optional filter function to accept/reject files
	 * Return true to accept the file, false to reject
	 */
	acceptFile?: (file: File) => boolean;
}

interface UseFileDropReturn {
	/**
	 * Whether a file is currently being dragged over the element
	 */
	isDragging: boolean;

	/**
	 * Ref to attach to the target element (optional, mainly for accessing the element)
	 */
	dropRef: React.RefObject<HTMLElement>;

	/**
	 * Event handlers to spread on the target element
	 * These must be spread on the element for the hook to work
	 */
	dropHandlers: {
		onDragOver: (e: React.DragEvent) => void;
		onDragEnter: (e: React.DragEvent) => void;
		onDragLeave: (e: React.DragEvent) => void;
		onDrop: (e: React.DragEvent) => void;
	};
}

export const useFileDrop = (options: UseFileDropOptions = {}): UseFileDropReturn => {
	const { onDropped, enabled = true, cancelOnEscape = false, acceptFile } = options;

	const [isDragging, setIsDragging] = useState(false);
	const dropRef = useRef<HTMLElement>(null);
	const dragCounterRef = useRef(0);
	const dragCancelledRef = useRef(false);
	const globalDragActiveRef = useRef(false);

	// Track global drag operations (across the entire document)
	useEffect(() => {
		if (!enabled || !cancelOnEscape) return;

		const handleDragStart = (e: DragEvent) => {
			// Only track file drags
			if (e.dataTransfer?.types.includes("Files")) {
				globalDragActiveRef.current = true;
			}
		};

		const handleDragEnd = () => {
			globalDragActiveRef.current = false;
		};

		document.addEventListener("dragstart", handleDragStart);
		document.addEventListener("dragend", handleDragEnd);

		return () => {
			document.removeEventListener("dragstart", handleDragStart);
			document.removeEventListener("dragend", handleDragEnd);
		};
	}, [enabled, cancelOnEscape]);

	// Handle Escape key to cancel drag operation
	// Always listen when cancelOnEscape is enabled and there's an active drag (even if not over our element)
	useEffect(() => {
		if (!enabled || !cancelOnEscape) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape" && globalDragActiveRef.current) {
				dragCancelledRef.current = true;
				setIsDragging(false);
				dragCounterRef.current = 0;
				globalDragActiveRef.current = false;
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [enabled, cancelOnEscape]);

	// Reset drag state if drag operation ends (e.g., user releases mouse outside window)
	useEffect(() => {
		if (!enabled) return;

		const handleDragEnd = () => {
			globalDragActiveRef.current = false;
			// Small delay to allow drop event to fire first if it's going to
			setTimeout(() => {
				if (dragCounterRef.current > 0) {
					dragCounterRef.current = 0;
					setIsDragging(false);
					dragCancelledRef.current = false;
				}
			}, 100);
		};

		document.addEventListener("dragend", handleDragEnd);
		return () => {
			document.removeEventListener("dragend", handleDragEnd);
		};
	}, [enabled]);

	const handleDragOver = useCallback(
		(e: React.DragEvent) => {
			if (!enabled) return;
			e.preventDefault();
			e.stopPropagation();
		},
		[enabled],
	);

	const handleDragEnter = useCallback(
		(e: React.DragEvent) => {
			if (!enabled) return;
			e.preventDefault();
			e.stopPropagation();

			// Only handle file drags
			if (!e.dataTransfer.types.includes("Files")) return;

			const target = e.currentTarget as HTMLElement;
			const relatedTarget = e.relatedTarget as Node;

			// Check if this is a fresh entry from outside (counter will be 0 before increment)
			const isFreshEntry = dragCounterRef.current === 0;

			// Increment counter for every dragenter
			dragCounterRef.current++;

			// Only set isDragging to true when entering from outside (not from a child)
			// This ensures isDragging reflects whether we're actually over the element
			if (!relatedTarget || !target.contains(relatedTarget)) {
				// If entering from outside and drag was previously cancelled, reset the flag
				// This allows re-entry after ESC was pressed (user dragged away and back)
				if (isFreshEntry && dragCancelledRef.current) {
					dragCancelledRef.current = false;
				}
				setIsDragging(true);
			}
		},
		[enabled],
	);

	const handleDragLeave = useCallback(
		(e: React.DragEvent) => {
			if (!enabled) return;
			e.preventDefault();
			e.stopPropagation();

			const target = e.currentTarget as HTMLElement;
			const relatedTarget = e.relatedTarget as Node;

			// Decrement counter for every dragleave
			dragCounterRef.current--;

			// Only set isDragging to false when actually leaving the element (not moving to a child)
			// When counter reaches 0, we've left the element completely
			if (!relatedTarget || !target.contains(relatedTarget)) {
				if (dragCounterRef.current <= 0) {
					dragCounterRef.current = 0;
					setIsDragging(false);
					dragCancelledRef.current = false;
				}
			}
		},
		[enabled],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			if (!enabled) return;

			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);
			dragCounterRef.current = 0;

			// Ignore drop if drag was cancelled by Escape key
			if (dragCancelledRef.current) {
				dragCancelledRef.current = false;
				return;
			}

			const files = Array.from(e.dataTransfer.files || []);
			if (files.length === 0) return;

			// Filter files if acceptFile function is provided
			const acceptedFiles = acceptFile ? files.filter(acceptFile) : files;

			if (acceptedFiles.length > 0 && onDropped) {
				onDropped(acceptedFiles);
			}

			// Clear data transfer
			e.dataTransfer.clearData();
		},
		[enabled, onDropped, acceptFile],
	);

	return {
		isDragging,
		dropRef,
		dropHandlers: {
			onDragOver: handleDragOver,
			onDragEnter: handleDragEnter,
			onDragLeave: handleDragLeave,
			onDrop: handleDrop,
		},
	};
};
